import { useMemo } from 'react';
import {
  categorySummary,
  categoryValues,
  ModelFilterCategory,
  useModelFilters,
} from '@store/zustand/model-filters';
import {
  CATEGORY_GROUPS,
  CATEGORY_LABELS,
} from '@views/Project/components/ModelFiltersPopover/categories';
import { useCategoryOptions } from '@views/Project/components/ModelFiltersPopover/useCategoryOptions';

export type AppliedFilterChipModel = {
  category: ModelFilterCategory;
  label: string;
  summary: string;
  values: string[];
  labels: Record<string, string>;
};

/**
 * The active filter categories as chip models, in the root popover's category
 * order.
 *
 * One model per category that carries a selection and that the project offers
 * — never one per value — with the summary computed once here, so the chip and
 * its +N popover row show the same text. A category is offered unless
 * `availability` says `false` for it, the same reading the root panel applies,
 * so a chip can only exist where its root row does. Selection order plays no
 * part: a chip's place is its category's place in `CATEGORY_GROUPS`.
 */
const useAppliedFilterChips = (): AppliedFilterChipModel[] => {
  const state = useModelFilters();
  const { labels, availability } = useCategoryOptions();

  return useMemo(
    () =>
      CATEGORY_GROUPS.flat()
        .filter((category) => availability[category] !== false)
        .map((category) => ({
          category,
          values: categoryValues(state, category),
        }))
        .filter(({ values }) => values.length > 0)
        .map(({ category, values }) => {
          const label = CATEGORY_LABELS[category];
          const valueLabels = labels[category] ?? {};

          return {
            category,
            label,
            values,
            labels: valueLabels,
            summary: categorySummary(values, valueLabels, label),
          };
        }),
    [state, labels, availability],
  );
};

export default useAppliedFilterChips;
